/* ==========================================================================
   HOUND RELIC PDP
   ==========================================================================
   Three small behaviours for sections/hound-relic-pdp.liquid:

     1. the 360 turntable
     2. the A/B model swap on the hero figure
     3. keeping the selected-size label and the mobile price in step with the
        variant radios

   Everything variant-related still belongs to the theme's own <variant-selects>
   element: it owns the hidden form input, the URL, availability, and the price
   inside the Add to cart button. This file only mirrors the two labels that
   element does not know about, and it re-binds after that element swaps the
   picker markup out from under it.
   ========================================================================== */
(function () {
  'use strict';

  /* Auto-rotation cadence. 90ms is about 11fps, which reads as a turntable
     rather than as video: fast enough to be obviously moving, slow enough that
     the eye can still register the garment at each angle. */
  var FRAME_MS = 90;

  /* 7px of drag per frame, so a full 48 frame turn is a 336px swipe. That is a
     comfortable thumb travel on a phone and a short flick on a trackpad. */
  var DRAG_PX = 7;

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function parseJSONTag(root, sel) {
    var tag = $(sel, root);
    if (!tag) return [];
    try {
      var v = JSON.parse(tag.textContent || '[]');
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  /* ------------------------------------------------------------------
     360 TURNTABLE
     ------------------------------------------------------------------
     Auto-rotates on arrival and stops permanently the first time the user
     touches it. Car configurators do not explain their turntables and they do
     not resume spinning once you have taken hold of the thing, because the
     whole point of grabbing it is to stop on the angle you wanted.
     ------------------------------------------------------------------ */
  function initSpin(root) {
    var band = $('[data-hrp-spin]', root);
    if (!band) return;

    var stage = $('[data-hrp-stage]', band);
    var img = $('[data-hrp-spin-frame]', band);
    var rail = $('[data-hrp-spin-rail]', band);
    if (!stage || !img) return;

    var sets = {
      a: parseJSONTag(band, '[data-hrp-frames-a]'),
      b: parseJSONTag(band, '[data-hrp-frames-b]')
    };

    /* Which spin set is which MODEL. The letters do not line up with the video
       slots: set B is always Fiona by naming convention, while video slot A is
       Fiona on the products where the female shot leads, so matching letter to
       letter would turn her garment on his body. Match on who, not on which. */
    var who = {
      a: stage.getAttribute('data-who-a') || 'male',
      b: stage.getAttribute('data-who-b') || 'female'
    };

    function setFor(model) {
      if (who.a === model && sets.a.length) return 'a';
      if (who.b === model && sets.b.length) return 'b';
      return sets.a.length ? 'a' : 'b';
    }

    /* Open on whichever model the hero figure is already showing. */
    var key = setFor(stage.getAttribute('data-start-who') || 'male');
    var urls = sets[key];
    if (!urls.length) {
      band.hidden = true;
      return;
    }

    var loaded = [];
    var frame = 0;
    var auto = true;
    var touched = false;
    var timer = 0;
    var dragging = false;
    var startX = 0;
    var startFrame = 0;
    /* Per-page cadence (data-hrp-frame-ms) and a short hold the CCTV layer asks
       for when a detail faces the camera (24 Oct), so its close-up can be read. */
    var frameMs = parseInt(band.getAttribute('data-hrp-frame-ms'), 10) || FRAME_MS;
    var holdUntil = 0;

    function preload(list) {
      loaded = new Array(list.length);
      list.forEach(function (url, i) {
        var im = new Image();
        im.onload = function () {
          loaded[i] = true;
          /* If the frame we are already sitting on has only just arrived, paint
             it now rather than waiting for the next tick. */
          if (i === frame) img.src = url;
        };
        im.src = url;
      });
    }

    /* Never blank mid-drag: if the exact frame has not downloaded yet, walk
       outwards for the closest one that has. A held frame reads as a slightly
       stiff rotation; an empty box reads as broken. */
    function nearestLoaded(i) {
      var n = urls.length;
      if (loaded[i]) return i;
      for (var d = 1; d < n; d++) {
        var up = (i + d) % n;
        var dn = (i - d + n) % n;
        if (loaded[up]) return up;
        if (loaded[dn]) return dn;
      }
      return i;
    }

    function paint() {
      var i = nearestLoaded(frame);
      if (urls[i]) img.src = urls[i];
      if (rail) rail.style.width = ((frame + 1) / urls.length) * 100 + '%';
    }

    function tick() {
      if (!auto) return;
      if (holdUntil && Date.now() < holdUntil) return;
      frame = (frame + 1) % urls.length;
      paint();
    }

    function start() {
      if (timer || !auto) return;
      timer = setInterval(tick, frameMs);
    }

    function stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = 0;
    }

    /* Only spin while the band is actually on screen and the tab is in front.
       The desktop band is 840px tall and sits well below the fold; running an
       11fps image swap against a viewport nobody is looking at costs battery
       and buys nothing. */
    var onScreen = false;
    function sync() {
      if (onScreen && auto && document.visibilityState === 'visible') start();
      else stop();
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          onScreen = entries[0].isIntersecting;
          sync();
        },
        { threshold: 0.05 }
      ).observe(band);
    } else {
      onScreen = true;
      sync();
    }
    document.addEventListener('visibilitychange', sync);

    function grab(e) {
      dragging = true;
      startX = e.clientX;
      startFrame = frame;
      auto = false;
      if (!touched) {
        touched = true;
        band.setAttribute('data-touched', '');
      }
      stop();
      if (stage.setPointerCapture && e.pointerId != null) {
        try {
          stage.setPointerCapture(e.pointerId);
        } catch (err) {
          /* Safari throws when the pointer is already captured elsewhere. */
        }
      }
    }

    function move(e) {
      if (!dragging) return;
      var n = urls.length;
      var step = Math.round((e.clientX - startX) / DRAG_PX);
      /* Dragging right turns the model the way a real turntable would, which
         means walking backwards through the frame sequence. */
      frame = (((startFrame - step) % n) + n) % n;
      paint();
    }

    function release() {
      dragging = false;
    }

    stage.addEventListener('pointerdown', grab);
    stage.addEventListener('pointermove', move);
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);

    stage.setAttribute('tabindex', '0');
    stage.setAttribute('role', 'img');
    stage.addEventListener('keydown', function (e) {
      var n = urls.length;
      var d = 0;
      if (e.key === 'ArrowRight') d = -1;
      else if (e.key === 'ArrowLeft') d = 1;
      else return;
      e.preventDefault();
      auto = false;
      stop();
      if (!touched) {
        touched = true;
        band.setAttribute('data-touched', '');
      }
      frame = (((frame + d) % n) + n) % n;
      paint();
    });

    /* Exposed so the model toggle can swap the frame set under the viewer. The two
       models are registered to the same anchor but stand at different heights in
       frame, so the geometry travels with the set. */
    band.__hrpSetModel = function (model) {
      var which = setFor(model);
      var next = sets[which];
      if (!next || !next.length || which === key) return;
      key = which;
      urls = next;
      frame = 0;
      var geo = (stage.getAttribute('data-spin-' + which) || '').split('|');
      if (geo.length === 2) {
        img.style.setProperty('--hrp-spin-h', geo[0] + '%');
        img.style.setProperty('--hrp-spin-b', geo[1] + '%');
      }
      preload(urls);
      paint();
    };

    band.__hrpHold = function (ms) {
      if (auto) holdUntil = Date.now() + ms;
    };

    paint();
    sync();
    /* Speed (24 Oct): the 48 frames are ~2MB. Only the first frame loads with the page; the rest
       start once the 360 band is near the screen or the page has finished loading, so they never
       compete with the model video and the first paint. */
    var warmed = false;
    function warm() { if (warmed) return; warmed = true; preload(urls); }
    if ('IntersectionObserver' in window) {
      var wio = new IntersectionObserver(function (es) {
        if (es[0].isIntersecting) { wio.disconnect(); warm(); }
      }, { rootMargin: '200px 0px' });
      wio.observe(band);
    } else {
      warm();
    }
    /* ...or once the page has finished loading and gone quiet, whichever comes first. */
    function idleWarm() { setTimeout(function () { (window.requestIdleCallback || function (f) { f(); })(warm); }, 1500); }
    if (document.readyState === 'complete') idleWarm(); else window.addEventListener('load', idleWarm, { once: true });
  }

  /* ------------------------------------------------------------------
     A/B MODEL
     ------------------------------------------------------------------ */
  function initModel(root) {
    var toggle = $('.hrp-model-toggle', root);
    if (!toggle) return;

    var band = $('[data-hrp-spin]', root);

    /* The per-model geometry lives on each <video> in Liquid, and the window sizes
       itself for whichever model needs the most room, so a swap changes the figure
       without moving the frame around it. Nothing to recompute here. */
    toggle.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hrp-model]');
      if (!btn) return;
      var which = btn.getAttribute('data-hrp-model');

      Array.prototype.forEach.call(toggle.querySelectorAll('button'), function (b) {
        b.classList.toggle('is-on', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });

      Array.prototype.forEach.call(root.querySelectorAll('[data-hrp-video]'), function (v) {
        var on = v.getAttribute('data-hrp-video') === which;
        v.hidden = !on;
        if (on) {
          /* The B video ships preload="none" so it costs nothing until asked
             for; load it the first time it is actually shown. */
          if (!v.dataset.hrpLoaded) {
            v.load();
            v.dataset.hrpLoaded = '1';
          }
          /* Start the take from the top. Coming back to a model half way through
             its loop reads as a glitch rather than as a second angle on the
             garment, and the opening frames are the ones that were shot to
             introduce the fit. */
          try {
            v.currentTime = 0;
          } catch (err) {
            /* Not seekable yet on a first load; it will start at 0 anyway. */
          }
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });

      /* Hand the turntable the MODEL, not the slot letter. */
      if (band && band.__hrpSetModel) band.__hrpSetModel(btn.getAttribute('data-hrp-who') || which);
    });
  }

  /* ------------------------------------------------------------------
     SELECTED SIZE + MOBILE PRICE
     ------------------------------------------------------------------
     <variant-selects> replaces the picker markup wholesale on every change, so
     these listeners are delegated from the section root rather than bound to
     the radios themselves.
     ------------------------------------------------------------------ */
  function initVariant(root) {
    var out = $('[data-hrp-selected]', root);
    var priceOut = $('[data-hrp-price]', root);
    var data = null;
    try {
      var tag = $('[data-hrp-variants]', root);
      data = tag ? JSON.parse(tag.textContent) : null;
    } catch (e) {
      data = null;
    }
    if (!data) return;

    /* Assigning textContent replaces the node's child text node, which is itself a
       childList mutation. Writing unconditionally from inside a MutationObserver
       therefore re-triggers that observer forever, and the page's main thread never
       comes back. Write only on a real change. */
    function put(el, value) {
      if (el && el.textContent !== value) el.textContent = value;
    }

    function update() {
      var picked = root.querySelector('.hrp-fit input[type="radio"]:checked');
      if (!picked) return;
      var title = picked.value;
      var hit = data.variants.filter(function (v) {
        return v.title === title;
      })[0];

      put(out, title);
      if (hit) put(priceOut, hit.price);
    }

    root.addEventListener('change', function (e) {
      if (e.target && e.target.matches('.hrp-fit input[type="radio"]')) update();
    });

    /* <variant-selects> swaps its fieldsets in after its fetch resolves, which lands
       after the change event we just handled, so re-read once the new markup is in
       place or availability-driven copy stays a step behind.

       Watch the picker, NOT .hrp-fit: the two labels this updates are siblings of the
       picker inside .hrp-fit, so observing the wider element would have it watching
       its own writes. */
    var picker = $('.hrp-fit variant-selects', root);
    if (picker && 'MutationObserver' in window) {
      new MutationObserver(update).observe(picker, { childList: true, subtree: true });
    }

    update();
  }

  /* The threshold gate moved to snippets/hound-threshold-gate.liquid, which the
     shared header renders. It was bound to this section's root, so the same
     button in the header on the collection page had nothing listening to it. */

  /* ------------------------------------------------------------------
     CART COUNT
     ------------------------------------------------------------------
     The header is server rendered, so after an add-to-cart it kept saying
     CART 0 while the drawer showed the item. The theme publishes cartUpdate
     on its own bus; fall back to re-reading /cart.js if that bus is not up
     yet, since a stale count on the one control a buyer uses to check their
     order is worse than an extra request.
     ------------------------------------------------------------------ */
  function initCartCount(root) {
    var out = $('[data-hrp-cart-count]', root);
    if (!out) return;

    function paint(n) {
      if (typeof n === 'number' && String(n) !== out.textContent) out.textContent = n;
    }

    function refresh() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (c) {
          paint(c.item_count);
        })
        .catch(function () {});
    }

    if (window.theme && theme.pubsub && theme.pubsub.subscribe) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (payload) {
        var c = payload && payload.cartData;
        if (c && typeof c.item_count === 'number') paint(c.item_count);
        else refresh();
      });
    }

    /* The submit itself is handled by <product-form>, which posts and then opens
       the drawer; this just reads the result once that has settled. */
    root.addEventListener('click', function (e) {
      if (e.target.closest('[name="add"], .product-form__submit')) setTimeout(refresh, 1200);
    });
  }

  /* ------------------------------------------------------------------
     ANCHORED DISCLOSURE
     ------------------------------------------------------------------
     The figure is position: sticky inside its grid area, and that area is only
     as tall as the taller column. With Size Guide and Care closed the dossier
     is SHORTER than the figure, so the figure has almost no sticky range: scroll
     to the panels and it has already ridden up with the page. Opening a panel
     lengthens the column, hands the range back, and the figure snaps to its
     pinned offset. Measured on Onyx SS at 1440x900: the video jumped 250px the
     instant Size Guide opened.

     Sticky is right once the dossier is long, so the answer is not to drop it.
     Instead the scroll position absorbs the change: measure where the figure sits
     before the panel opens, and put it back afterwards. The panel opens and the
     product does not move, which is what a shopper expects from a disclosure.

     Compensation runs for a few frames rather than once, because a disclosure
     that animates its height moves the figure on every frame of the animation.
     It stops as soon as two consecutive frames need no correction, so it can
     never sit in a loop fighting a scroll the shopper is making themselves.
     ------------------------------------------------------------------ */
  function initDisclosure(root) {
    var figure = $('.hrp-figure', root);
    if (!figure) return;

    function top() {
      return figure.getBoundingClientRect().top;
    }

    root.addEventListener('click', function (e) {
      var summary = e.target.closest('summary');
      if (!summary || !root.contains(summary)) return;

      var before = top();
      var settled = 0;
      var frames = 0;

      (function correct() {
        var delta = top() - before;
        if (Math.abs(delta) > 0.5) {
          window.scrollBy(0, delta);
          settled = 0;
        } else {
          settled += 1;
        }
        /* ~40 frames is a comfortable ceiling over any disclosure animation. */
        if (settled < 2 && ++frames < 40) requestAnimationFrame(correct);
      })();
    });
  }

  function boot(root) {
    if (!root || root.__hrpBooted) return;
    root.__hrpBooted = true;
    initSpin(root);
    initModel(root);
    initVariant(root);
    initCartCount(root);
    initDisclosure(root);
    initAutoplayRescue(root);
  }

  /* ------------------------------------------------------------------
     AUTOPLAY RESCUE
     ------------------------------------------------------------------
     The hero carries autoplay/muted/playsinline, but two real-world paths
     reject declarative autoplay and leave the webview's native play button
     sitting on a frozen hero: iOS LOW POWER MODE (blocks all autoplay
     until a user gesture) and the Instagram/Meta in-app browser - which is
     exactly where the ad traffic this page exists for arrives from
     (Evan's report, Aug 24, battery at 19% in the screenshot). play() is
     retried when data arrives and when the page is shown, and the FIRST
     touch or click anywhere unlocks every still-paused hero: in LPM a
     gesture-initiated play is permitted, so the video starts the moment
     the visitor interacts at all.
     ------------------------------------------------------------------ */
  function initAutoplayRescue(root) {
    var vids = Array.prototype.slice.call(root.querySelectorAll('video[autoplay]'));
    if (!vids.length) return;
    function tryPlay() {
      vids.forEach(function (v) {
        if (v.hidden || !v.paused) return;
        /* The PROPERTY, not just the attribute: some iOS webviews evaluate
           the mute gate off the property at play() time. */
        v.muted = true;
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      });
    }
    vids.forEach(function (v) {
      v.addEventListener('loadedmetadata', tryPlay);
      v.addEventListener('canplay', tryPlay);
    });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) tryPlay();
    });
    function unlock() {
      tryPlay();
      document.removeEventListener('touchend', unlock);
      document.removeEventListener('click', unlock);
    }
    /* touchend and click are the gestures Safari honours for media unlock. */
    document.addEventListener('touchend', unlock, { passive: true });
    document.addEventListener('click', unlock);
    tryPlay();
  }

  function scan() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-hrp]'), boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  /* Theme editor: a section re-render replaces the whole node. */
  document.addEventListener('shopify:section:load', scan);
})();
